import { useState, useEffect, useRef } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Upload, Trash2, Image, GripVertical, X, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Max file size: 2MB for web optimization
const MAX_FILE_SIZE = 2 * 1024 * 1024;

export default function GalleryManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setImages(res.data);
    } catch (err) {
      console.error("Failed to fetch gallery images:", err);
      toast.error("Failed to load gallery images");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - JPEG only
    if (!file.type.includes("jpeg") && !file.type.includes("jpg")) {
      toast.error("Please select a JPEG image file");
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error("Image must be under 2MB. Please compress or resize the image.");
      return;
    }

    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select an image first");
      return;
    }

    setUploading(true);
    
    try {
      // Convert file to base64 for simple storage
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        
        try {
          await axios.post(`${API}/gallery`, {
            url: base64Data,
            caption: caption || "Gallery Image"
          });
          
          toast.success("Image uploaded successfully!");
          
          // Reset form
          setSelectedFile(null);
          setPreviewUrl(null);
          setCaption("");
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          
          // Refresh images
          fetchImages();
        } catch (err) {
          console.error("Failed to upload image:", err);
          toast.error("Failed to upload image");
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(selectedFile);
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to process image");
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) {
      return;
    }

    try {
      await axios.delete(`${API}/gallery/${imageId}`);
      toast.success("Image deleted");
      setImages(images.filter(img => img.id !== imageId));
    } catch (err) {
      console.error("Failed to delete image:", err);
      toast.error("Failed to delete image");
    }
  };

  const cancelUpload = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setCaption("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#3A5F40]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Upload Section */}
      <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Upload size={24} className="text-[#3A5F40]" />
          <h2 className="text-xl text-[#1C1C1C]">Upload New Image</h2>
        </div>
        
        <div className="space-y-4">
          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-[#666055] mb-2">
              Select Image (JPEG only, max 2MB)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,image/jpeg"
              onChange={handleFileSelect}
              className="block w-full text-sm text-[#666055] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-[#3A5F40]/10 file:text-[#3A5F40] hover:file:bg-[#3A5F40]/20 cursor-pointer"
              data-testid="gallery-file-input"
            />
          </div>

          {/* Preview */}
          {previewUrl && (
            <div className="relative">
              <label className="block text-sm font-medium text-[#666055] mb-2">
                Preview
              </label>
              <div className="relative inline-block">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-h-48 rounded-lg border border-[#E6E2D6]"
                  data-testid="gallery-preview"
                />
                <button
                  onClick={cancelUpload}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  data-testid="cancel-preview-btn"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Caption */}
          {previewUrl && (
            <div>
              <label className="block text-sm font-medium text-[#666055] mb-2">
                Caption (optional)
              </label>
              <Input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Enter a caption for this image"
                className="max-w-md"
                data-testid="gallery-caption-input"
              />
            </div>
          )}

          {/* Upload Button */}
          {previewUrl && (
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="bg-[#3A5F40] hover:bg-[#2A4430] text-white rounded-full"
              data-testid="gallery-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={18} className="mr-2" />
                  Upload Image
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Current Images */}
      <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Image size={24} className="text-[#C5A059]" />
          <h2 className="text-xl text-[#1C1C1C]">
            Gallery Images ({images.length})
          </h2>
        </div>

        {images.length === 0 ? (
          <div className="text-center py-12 text-[#666055]">
            <Image size={48} className="mx-auto mb-4 opacity-30" />
            <p>No images in the gallery yet</p>
            <p className="text-sm mt-2">Upload your first image above</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <div
                key={image.id}
                className="relative group rounded-xl overflow-hidden border border-[#E6E2D6] bg-[#F9F8F4]"
                data-testid={`gallery-item-${index}`}
              >
                <div className="aspect-square">
                  <img
                    src={image.url}
                    alt={image.caption || `Gallery image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Caption Overlay */}
                {image.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                    <p className="text-white text-sm truncate">{image.caption}</p>
                  </div>
                )}

                {/* Delete Button */}
                <button
                  onClick={() => handleDelete(image.id)}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  data-testid={`delete-gallery-item-${index}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-[#3A5F40]/5 rounded-2xl p-6 md:p-8">
        <h3 className="text-lg font-medium text-[#1C1C1C] mb-4">Image Guidelines</h3>
        <ul className="space-y-2 text-[#666055]">
          <li className="flex gap-2">
            <span className="font-medium text-[#3A5F40]">•</span>
            Format: JPEG images only (.jpg, .jpeg)
          </li>
          <li className="flex gap-2">
            <span className="font-medium text-[#3A5F40]">•</span>
            Maximum file size: 2MB per image
          </li>
          <li className="flex gap-2">
            <span className="font-medium text-[#3A5F40]">•</span>
            Recommended dimensions: 1200x800 pixels or larger
          </li>
          <li className="flex gap-2">
            <span className="font-medium text-[#3A5F40]">•</span>
            Images will appear on the Venue page gallery
          </li>
          <li className="flex gap-2">
            <span className="font-medium text-[#3A5F40]">•</span>
            Hover over an image and click the trash icon to delete
          </li>
        </ul>
      </div>
    </div>
  );
}
